import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api } from "../../lib/api";
import { fmtRoDate } from "../../lib/dateFmt";
import SuppliersDialog from "./SuppliersDialog";
import StoresDialog from "./StoresDialog";

const STATUS_TABS = [
  { key: "pending",   label: "⏳ În așteptare", color: "amber" },
  { key: "received",  label: "✅ Primite",       color: "emerald" },
  { key: "cancelled", label: "✗ Anulate",         color: "zinc" },
];

const EMPTY_FORM = {
  supplier: "",
  item_name: "",
  item_id: "",
  quantity: 1,
  destination_store_id: "",
  destination_text: "",
  note: "",
};

export default function OrdersDialog({ open, onClose, stores, items, onChanged }) {
  const [tab, setTab] = useState("pending");
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  // Receive dialog state
  const [receivingOrder, setReceivingOrder] = useState(null);
  const [receivedQty, setReceivedQty] = useState(0);
  const [receiveTarget, setReceiveTarget] = useState({ mode: "existing", target_item_id: "" });
  const [missingStrategy, setMissingStrategy] = useState("keep_pending");
  // German summary + suppliers manager
  const [suppliersOpen, setSuppliersOpen] = useState(false);
  const [storesOpen, setStoresOpen] = useState(false);
  // WhatsApp state
  const [waPreview, setWaPreview] = useState(null); // { text, whatsapp_url, supplier_phone, supplier_name }
  const [selectedIds, setSelectedIds] = useState(new Set()); // bulk selection (pending tab)

  const load = async () => {
    if (!open) return;
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get(`/inventory/orders?status=${tab}`),
        api.get(`/inventory/suppliers`).catch(() => ({ data: [] })),
      ]);
      setOrders(r.data || []);
      setSuppliers(s.data || []);
    } catch {
      toast.error("Eroare la încărcarea comenzilor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); setSelectedIds(new Set()); }, [open, tab]);

  // Default warehouse store (preselectat în formularul comandă)
  const defaultStore = useMemo(() => stores.find((s) => s.is_warehouse), [stores]);
  useEffect(() => {
    if (open && defaultStore && !form.destination_store_id && !form.destination_text) {
      setForm((f) => ({ ...f, destination_store_id: defaultStore.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultStore?.id]);

  const supplierOptions = useMemo(() => {
    const set = new Set();
    for (const s of suppliers) if (s.name) set.add(s.name);
    for (const o of orders) if (o.supplier) set.add(o.supplier);
    return Array.from(set).sort();
  }, [orders, suppliers]);

  const submitForm = async () => {
    if (!form.supplier.trim()) { toast.error("Furnizorul e obligatoriu"); return; }
    if (!form.item_name.trim()) { toast.error("Articolul e obligatoriu"); return; }
    if (Number(form.quantity) <= 0) { toast.error("Cantitatea trebuie > 0"); return; }
    if (!form.destination_store_id && !form.destination_text.trim()) {
      toast.error("Setează destinația (magazie sau text)"); return;
    }
    setBusy(true);
    try {
      const payload = {
        supplier: form.supplier.trim(),
        item_name: form.item_name.trim(),
        item_id: form.item_id || null,
        quantity: Number(form.quantity),
        destination_store_id: form.destination_store_id || null,
        destination_text: form.destination_text.trim() || null,
        note: form.note.trim() || null,
      };
      await api.post("/inventory/orders", payload);
      toast.success("✓ Comandă creată");
      setForm(EMPTY_FORM);
      setTab("pending");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la creare");
    } finally {
      setBusy(false);
    }
  };

  const openReceive = (order) => {
    setReceivingOrder(order);
    setReceivedQty(order.quantity);
    setReceiveTarget({
      mode: order.item_id ? "existing" : "new",
      target_item_id: order.item_id || "",
    });
    setMissingStrategy("keep_pending");
  };

  const submitReceive = async () => {
    if (!receivingOrder) return;
    if (Number(receivedQty) <= 0) { toast.error("Cantitatea trebuie > 0"); return; }
    if (receiveTarget.mode === "existing" && !receiveTarget.target_item_id) {
      toast.error("Selectează un item existent sau alege „Creează nou"); return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/inventory/orders/${receivingOrder.id}/receive`, {
        received_qty: Number(receivedQty),
        target_item_id: receiveTarget.mode === "existing" ? receiveTarget.target_item_id : null,
        create_new_item: receiveTarget.mode === "new",
        missing_strategy: missingStrategy,
      });
      const missing = r.data?.missing_qty || 0;
      if (missing > 0 && missingStrategy === "keep_pending") {
        toast.success(`✓ Recepție parțială — am creat comandă follow-up pentru ${missing} buc lipsă`);
      } else if (missing > 0) {
        toast.success(`✓ Recepție parțială — ${missing} buc abandonate`);
      } else {
        toast.success("✓ Comandă primită — stocul a fost actualizat");
      }
      setReceivingOrder(null);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la primire");
    } finally {
      setBusy(false);
    }
  };

  const openWhatsAppSingle = async (order) => {
    setBusy(true);
    try {
      const r = await api.post(`/inventory/orders/${order.id}/whatsapp`);
      setWaPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare WhatsApp");
    } finally {
      setBusy(false);
    }
  };

  const openWhatsAppBulk = async () => {
    if (selectedIds.size === 0) { toast.error("Selectează cel puțin o comandă"); return; }
    const ids = Array.from(selectedIds);
    // Verifică același furnizor
    const sels = orders.filter((o) => ids.includes(o.id));
    const uniqSup = new Set(sels.map((o) => o.supplier));
    if (uniqSup.size > 1) {
      toast.error("Toate comenzile selectate trebuie să fie de la același furnizor");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/inventory/orders/whatsapp-bulk`, { order_ids: ids });
      setWaPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare WhatsApp");
    } finally {
      setBusy(false);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const cancelOrder = async (order) => {
    const reason = window.prompt(`Anulează comanda „${order.item_name}" de la ${order.supplier}?\nMotiv (opțional):`, "");
    if (reason === null) return;
    setBusy(true);
    try {
      await api.post(`/inventory/orders/${order.id}/cancel`, { reason: reason || null });
      toast.success("Comanda a fost anulată");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally {
      setBusy(false);
    }
  };

  const destinationLabel = (o) => {
    if (o.destination_store_id) {
      const s = stores.find((x) => x.id === o.destination_store_id);
      return s ? `🏪 ${s.name}` : "🏪 Magazie";
    }
    return o.destination_text ? `📍 ${o.destination_text}` : "—";
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="orders-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 pr-6">
            <DialogTitle>🛒 Comenzi inventar</DialogTitle>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setStoresOpen(true)}
                className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="open-stores-btn"
                title="Configurează depozitul principal (adresă de livrare default)"
              >
                🏪 Magazii ({stores.length})
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSuppliersOpen(true)}
                className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="open-suppliers-btn"
              >
                🏢 Furnizori ({suppliers.length})
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Form NEW ORDER */}
        <div className="border-2 border-emerald-300 bg-emerald-50/50 rounded-lg p-3 mb-3">
          <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-800 mb-2">➕ Comandă nouă</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="small-label">🏢 Furnizor</Label>
              <Input
                value={form.supplier}
                onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                placeholder="ex: OBI Suceava · Hornbach"
                list="supplier-suggestions"
                data-testid="order-supplier"
              />
              <datalist id="supplier-suggestions">
                {supplierOptions.map((s) => <option key={s} value={s} />)}
              </datalist>
            </div>
            <div>
              <Label className="small-label">📦 Articol</Label>
              <Input
                value={form.item_name}
                onChange={(e) => setForm({ ...form, item_name: e.target.value, item_id: "" })}
                placeholder="ex: Plăcuțe frână Audi A4"
                list="item-suggestions"
                data-testid="order-item-name"
              />
              <datalist id="item-suggestions">
                {items.map((it) => <option key={it.id} value={it.name} />)}
              </datalist>
              {/* Asociere opțională cu item existent */}
              <Select
                value={form.item_id || "_none"}
                onValueChange={(v) => {
                  if (v === "_none") return setForm({ ...form, item_id: "" });
                  const it = items.find((x) => x.id === v);
                  setForm({ ...form, item_id: v, item_name: it?.name || form.item_name });
                }}
              >
                <SelectTrigger className="mt-1 h-7 text-xs" data-testid="order-item-link">
                  <SelectValue placeholder="🔗 Asociază cu item existent (opțional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— niciunul (text liber) —</SelectItem>
                  {items.slice(0, 200).map((it) => (
                    <SelectItem key={it.id} value={it.id}>{it.name} ({it.location || "?"})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="small-label">🔢 Cantitate</Label>
              <Input
                type="number"
                min={1}
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                data-testid="order-quantity"
              />
            </div>
            <div>
              <Label className="small-label">📍 Destinație</Label>
              <Select
                value={form.destination_store_id || "_other"}
                onValueChange={(v) => setForm({ ...form, destination_store_id: v === "_other" ? "" : v, destination_text: v === "_other" ? form.destination_text : "" })}
              >
                <SelectTrigger data-testid="order-destination-store">
                  <SelectValue placeholder="Selectează magazie sau Alta" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map((s) => (
                    <SelectItem key={s.id} value={s.id}>🏪 {s.name}{s.is_warehouse ? " (depozit principal)" : ""}</SelectItem>
                  ))}
                  <SelectItem value="_other">📝 Alta (text liber)</SelectItem>
                </SelectContent>
              </Select>
              {!form.destination_store_id && (
                <Input
                  value={form.destination_text}
                  onChange={(e) => setForm({ ...form, destination_text: e.target.value })}
                  placeholder="ex: Garaj Constantin · Șantier Bischof"
                  className="mt-1 h-7 text-xs"
                  data-testid="order-destination-text"
                />
              )}
            </div>
            <div className="md:col-span-2">
              <Label className="small-label">📝 Notă (opțional)</Label>
              <Textarea
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="ex: Urgent · să sune înainte"
                className="min-h-[40px] text-sm"
                data-testid="order-note"
              />
            </div>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setForm(EMPTY_FORM)} data-testid="order-form-reset">
              Resetează
            </Button>
            <Button size="sm" onClick={submitForm} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="order-submit">
              {busy ? "Se salvează…" : "✓ Plasează comanda"}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-zinc-200 mb-2">
          {STATUS_TABS.map((t) => {
            const isActive = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`text-sm font-semibold px-3 py-1.5 border-b-2 ${isActive ? `border-${t.color}-600 text-${t.color}-700` : "border-transparent text-zinc-500 hover:text-zinc-700"}`}
                data-testid={`orders-tab-${t.key}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Bulk WhatsApp action bar (pending tab) */}
        {tab === "pending" && selectedIds.size > 0 && (
          <div className="flex items-center justify-between gap-2 mb-2 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-300" data-testid="orders-bulk-bar">
            <div className="text-sm font-semibold text-emerald-900">
              {selectedIds.size} {selectedIds.size === 1 ? "comandă selectată" : "comenzi selectate"}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedIds(new Set())}
                data-testid="orders-bulk-clear"
              >
                Anulează selecția
              </Button>
              <Button
                size="sm"
                className="bg-[#25D366] hover:bg-[#1ebe5c] text-white"
                onClick={openWhatsAppBulk}
                disabled={busy}
                data-testid="orders-bulk-whatsapp"
                title="Generează un singur mesaj WhatsApp combinat (toate comenzile selectate trebuie să fie de la același furnizor)"
              >
                💬 Trimite pe WhatsApp ({selectedIds.size})
              </Button>
            </div>
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto" data-testid="orders-list">
          {loading ? (
            <div className="text-center text-sm text-zinc-400 py-8">Se încarcă…</div>
          ) : orders.length === 0 ? (
            <div className="text-center text-sm text-zinc-400 italic py-8">Nicio comandă în această categorie.</div>
          ) : (
            <div className="space-y-2">
              {orders.map((o) => (
                <div key={o.id} className="border border-zinc-200 rounded-md p-3 bg-white hover:border-zinc-300" data-testid={`order-row-${o.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    {o.status === "pending" && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(o.id)}
                        onChange={() => toggleSelected(o.id)}
                        className="mt-1 w-4 h-4 accent-emerald-600 flex-shrink-0"
                        data-testid={`order-select-${o.id}`}
                        title="Selectează pentru acțiuni bulk (ex: WhatsApp combinat)"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-zinc-900">{o.item_name} <span className="text-zinc-400 font-normal">× {o.quantity}</span></div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">🏢 {o.supplier} · {destinationLabel(o)}</div>
                      {o.note && <div className="text-[11px] italic text-zinc-600 mt-1 bg-zinc-50 rounded px-2 py-1">📝 {o.note}</div>}
                      <div className="text-[10px] text-zinc-400 mt-1">
                        Plasată {fmtRoDate(o.created_at)} de {o.created_by}
                        {o.status === "received" && o.received_at && <span> · ✅ Primită {fmtRoDate(o.received_at)} (qty {o.received_qty}) de {o.received_by}</span>}
                        {o.status === "cancelled" && o.cancelled_at && <span> · ✗ Anulată {fmtRoDate(o.cancelled_at)} de {o.cancelled_by}{o.cancel_reason ? ` (${o.cancel_reason})` : ""}</span>}
                        {o.follow_up_of && <span> · 🔁 Follow-up din #{o.follow_up_of.slice(0, 8)}</span>}
                      </div>
                    </div>
                    {o.status === "pending" && (
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" onClick={() => openReceive(o)} data-testid={`order-receive-${o.id}`}>
                          ✓ Primit
                        </Button>
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-[#25D366] hover:bg-[#1ebe5c] text-white"
                          onClick={() => openWhatsAppSingle(o)}
                          data-testid={`order-whatsapp-${o.id}`}
                          title="Trimite comanda pe WhatsApp către furnizor"
                        >
                          💬 WhatsApp
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50" onClick={() => cancelOrder(o)} data-testid={`order-cancel-${o.id}`}>
                          ✗ Anulează
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Închide</Button>
        </DialogFooter>
      </DialogContent>

      {/* Receive sub-dialog */}
      {receivingOrder && (
        <Dialog open onOpenChange={() => setReceivingOrder(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>✓ Confirmă primire comandă</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="bg-zinc-50 rounded p-2 text-sm">
                <div className="font-semibold">{receivingOrder.item_name} × {receivingOrder.quantity}</div>
                <div className="text-[11px] text-zinc-500">🏢 {receivingOrder.supplier} · {destinationLabel(receivingOrder)}</div>
              </div>
              <div>
                <Label className="small-label">Cantitate primită</Label>
                <Input
                  type="number"
                  min={1}
                  max={receivingOrder.quantity}
                  value={receivedQty}
                  onChange={(e) => setReceivedQty(e.target.value)}
                  data-testid="receive-qty"
                />
                {Number(receivedQty) < Number(receivingOrder.quantity) && Number(receivedQty) > 0 && (
                  <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-[12px] space-y-1.5" data-testid="receive-missing-section">
                    <div className="font-semibold text-amber-900">
                      ⚠ Lipsesc {Number(receivingOrder.quantity) - Number(receivedQty)} buc din comandă
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="missing-strategy"
                        value="keep_pending"
                        checked={missingStrategy === "keep_pending"}
                        onChange={() => setMissingStrategy("keep_pending")}
                        className="mt-0.5"
                        data-testid="missing-strategy-keep"
                      />
                      <span><strong>Creează o comandă follow-up</strong> pentru diferență (rămâne în „În așteptare").</span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="missing-strategy"
                        value="cancel"
                        checked={missingStrategy === "cancel"}
                        onChange={() => setMissingStrategy("cancel")}
                        className="mt-0.5"
                        data-testid="missing-strategy-cancel"
                      />
                      <span>Renunță la diferență — comandă închisă parțial.</span>
                    </label>
                  </div>
                )}
              </div>
              <div>
                <Label className="small-label">Adaugă în inventar</Label>
                <Select value={receiveTarget.mode} onValueChange={(v) => setReceiveTarget({ ...receiveTarget, mode: v })}>
                  <SelectTrigger data-testid="receive-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="existing">🔗 La item existent</SelectItem>
                    <SelectItem value="new">✨ Creează item nou</SelectItem>
                  </SelectContent>
                </Select>
                {receiveTarget.mode === "existing" && (
                  <Select
                    value={receiveTarget.target_item_id || "_none"}
                    onValueChange={(v) => setReceiveTarget({ ...receiveTarget, target_item_id: v === "_none" ? "" : v })}
                  >
                    <SelectTrigger className="mt-2" data-testid="receive-target-item">
                      <SelectValue placeholder="Selectează item existent" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— alege item —</SelectItem>
                      {items.map((it) => (
                        <SelectItem key={it.id} value={it.id}>{it.name} (qty: {it.qty || 0} · {it.location || "?"})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReceivingOrder(null)}>Anulează</Button>
              <Button onClick={submitReceive} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="receive-confirm">
                {busy ? "Se procesează…" : "✓ Confirmă"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* WhatsApp preview dialog */}
      {waPreview && (
        <Dialog open onOpenChange={() => setWaPreview(null)}>
          <DialogContent className="max-w-lg" data-testid="whatsapp-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="text-[#25D366]">💬</span> Mesaj WhatsApp pentru {waPreview.supplier_name || "furnizor"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {waPreview.supplier_phone ? (
                <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                  ✓ Telefon furnizor: <strong>{waPreview.supplier_phone}</strong>
                </div>
              ) : (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  ⚠ Furnizorul nu are telefon salvat. La click pe „Deschide WhatsApp" va trebui să alegi destinatarul manual.
                </div>
              )}
              <div>
                <Label className="small-label">Conținut mesaj</Label>
                <Textarea
                  value={waPreview.text}
                  onChange={(e) => setWaPreview({ ...waPreview, text: e.target.value, whatsapp_url: waPreview.whatsapp_url.split("?")[0] + "?text=" + encodeURIComponent(e.target.value) })}
                  className="min-h-[180px] text-xs font-mono"
                  data-testid="whatsapp-text"
                />
                <div className="text-[10px] text-zinc-400 mt-1">Poți edita textul înainte de a-l trimite.</div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(waPreview.text);
                  toast.success("✓ Text copiat în clipboard");
                }}
                data-testid="whatsapp-copy"
              >
                📋 Copiază text
              </Button>
              <Button
                onClick={() => {
                  window.open(waPreview.whatsapp_url, "_blank", "noopener");
                  setWaPreview(null);
                }}
                className="bg-[#25D366] hover:bg-[#1ebe5c] text-white"
                data-testid="whatsapp-open"
              >
                💬 Deschide WhatsApp
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Eliminat — utilizatorul a optat doar pentru WhatsApp ca metodă de trimitere comandă */}

      {/* Suppliers manager */}
      <SuppliersDialog
        open={suppliersOpen}
        onClose={() => setSuppliersOpen(false)}
        onChanged={() => { load(); }}
      />

      {/* Stores manager */}
      <StoresDialog
        open={storesOpen}
        onClose={() => setStoresOpen(false)}
        onChanged={() => {
          load();
          onChanged && onChanged();
        }}
      />
    </Dialog>
  );
}
